import {Button, Col, Input, InputNumber, message, Modal, Row, Select, Table, Typography} from 'antd';
import {useEffect, useState} from 'react';
import {motion} from 'framer-motion';
import {DeleteOutlined} from '@ant-design/icons';
import axiosInstance from '../../../../variables/api/axiosInstance';
import {rightsService} from '../../../../services/rightsService';
import DefaultCommentSuggestions from "../DefaultCommentSuggestions";

const {TextArea} = Input;
const {Title, Text} = Typography;

const OrderModal = ({orderModalOpen, setOrderModalOpen, categories, onSubmitCommandeAttestation}) => {
    // State for the cart system
    const [cartItems, setCartItems] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [productQuantity, setProductQuantity] = useState(1);
    const [commentaire, setCommentaire] = useState('');
    const [produits, setProduits] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);

    useEffect(() => {
        setLoadingProducts(true);
        axiosInstance
            .get(`/api/produit-attestation`)
            .then((res) => {
                // Reverse to show the latest first if needed
                setProduits(res.data.reverse());
                setLoadingProducts(false);
            })
            .catch(() => setLoadingProducts(false));
    }, []);

    const getOrderState = () => (rightsService('HCO072') ? 'VALIDE' : 'INITIE');
    const getAction = () => (rightsService('HCO072') ? 'VALIDER' : 'INITIER');

    // Add the selected product to the cart.
    const handleAddToCart = () => {
        if (!selectedProduct || productQuantity <= 0) return;

        // If the product is already in the cart, just update its quantity.
        const existingIndex = cartItems.findIndex(
            (item) => item.product.historiqueId === selectedProduct.historiqueId
        );
        if (existingIndex > -1) {
            const updatedCart = [...cartItems];
            updatedCart[existingIndex].quantity += productQuantity;
            setCartItems(updatedCart);
        } else {
            setCartItems([...cartItems, {product: selectedProduct, quantity: productQuantity}]);
        }
        // Reset selection and quantity.
        setSelectedProduct(null);
        setProductQuantity(1);
    };

    // Update quantity directly in the cart table.
    const handleCartQuantityChange = (value, record) => {
        const updatedCart = cartItems.map((item) =>
            item.product.historiqueId === record.product.historiqueId
                ? {...item, quantity: value}
                : item
        );
        setCartItems(updatedCart);
    };

    // Remove a product from the cart.
    const handleRemoveFromCart = (record) => {
        setCartItems(cartItems.filter((item) => item.product.historiqueId !== record.product.historiqueId));
    };

    // Columns for the cart table.
    const columns = [
        {
            title: 'Produit',
            key: 'produit',
            render: (_, record) =>
                `${record.product.typeAttestation} - ${record.product.categorie?.nom || 'N/A'}`
        },
        {
            title: 'Prix Unitaire',
            key: 'montant',
            render: (_, record) => `${record.product.montant.toFixed(2)}`
        },
        {
            title: 'Quantité',
            key: 'quantity',
            render: (_, record) => (
                <InputNumber
                    min={1}
                    value={record.quantity}
                    onChange={(value) => handleCartQuantityChange(value, record)}
                />
            )
        },
        {
            title: 'Total',
            key: 'total',
            render: (_, record) => {
                const total = record.product.montant * record.quantity;
                return ` ${total.toFixed(2)}`;
            }
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Button danger onClick={() => handleRemoveFromCart(record)}>
                    <DeleteOutlined/>
                </Button>
            )
        }
    ];

    const overallTotal = cartItems.reduce(
        (sum, item) => sum + item.product.montant * item.quantity,
        0
    );

    // Save the entire order by mapping cart items to the API's expected format.
    const handleSaveAll = async () => {
        if (cartItems.length === 0) return;
        const produitAttestationCommandes = cartItems.map((item) => ({
            quantite: item.quantity,
            produitAttestation: item.product,
        }));

        const actionsSurCommande = [
            {
                commentaire: commentaire,
                action: getAction()
            }
        ]

        const bonDeCommande = {
            produitAttestationCommandes: produitAttestationCommandes,
            actionsSurCommande: actionsSurCommande,
            commentaire,
            statutCommande: getOrderState()
        };
        console.log(bonDeCommande)

        try {
            await axiosInstance
                .post(`/api/bon-commande`, bonDeCommande)
                .then((res) => {
                    message.success('Bon de commande ajouté avec succès !');
                    setCartItems([]);
                    onSubmitCommandeAttestation();
                    setOrderModalOpen(false);
                })
                .catch(() => {
                    message.error("Échec de l'envoi des données");
                });
        } catch (error) {
            message.error("Erreur lors de l'envoi des données");
        }
    };
    return (
        <Modal
            open={orderModalOpen}
            onCancel={() => setOrderModalOpen(false)}
            footer={null}
            zIndex={1900}
            centered
            width={700}
            className="order-modal"
        >
            <motion.div initial={{opacity: 0, y: -10}} animate={{opacity: 1, y: 0}} transition={{duration: 0.3}}>
                <Title level={3} style={{textAlign: 'center', marginBottom: 10, color: '#1890ff'}}>
                    Lancer une commande d'attestations
                </Title>
                <Text type="secondary"
                      style={{display: 'block', textAlign: 'center', marginBottom: 20, fontSize: '14px'}}>
                    Effectuer une commande de produits d'attestations
                </Text>
            </motion.div>

            {/* Section to add a product to the cart */}
            <div style={{marginBottom: '30px'}}>
                <Row gutter={[16, 16]}>
                    <Col span={16}>
                        <Title level={5} style={{color: '#1890ff'}}>
                            Ajouter un produit
                        </Title>
                        <Select
                            showSearch
                            style={{width: '100%'}}
                            placeholder="Sélectionnez un produit"
                            value={selectedProduct ? selectedProduct.historiqueId : undefined}
                            onChange={(value) => {
                                const prod = produits.find((p) => p.historiqueId === value);
                                setSelectedProduct(prod);
                            }}
                            optionFilterProp="children"
                            loading={loadingProducts}
                            filterOption={(input, option) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {produits.map((prod) => (
                                <Select.Option key={prod.historiqueId} value={prod.historiqueId}>
                                    {`${prod.typeAttestation}`}
                                </Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col span={8}>
                        <Title level={5} style={{color: '#1890ff'}}>
                            Saisir la quantité
                        </Title>
                        <InputNumber
                            min={1}
                            value={productQuantity}
                            onChange={(value) => setProductQuantity(value)}
                            placeholder="Quantité"
                            style={{width: '100%'}}
                        />
                    </Col>
                    <Col span={24}>
                        <Button type="primary" onClick={handleAddToCart} style={{width: '100%'}}>
                            Ajouter au panier
                        </Button>
                    </Col>
                </Row>
            </div>

            {/* Cart display */}
            {cartItems.length > 0 && (
                <div style={{marginBottom: '20px'}}>
                    <Title level={5} style={{color: '#1890ff'}}>
                        Panier
                    </Title>
                    <Table
                        dataSource={cartItems}
                        columns={columns}
                        rowKey={(record) => record.product.historiqueId}
                        pagination={false}
                    />
                    <div style={{textAlign: 'right', marginTop: '10px'}}>
                        <Title level={4}>Total: {overallTotal.toFixed(2)}</Title>
                    </div>
                </div>
            )}

            {/* Commentaire */}
            <div style={{marginBottom: '20px'}}>
                <label
                    style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#333',
                        marginBottom: '8px',
                        display: 'block'
                    }}
                >
                    Commentaire
                </label>
                <TextArea
                    rows={3}
                    value={commentaire}
                    onChange={(e) => setCommentaire(e.target.value)}
                    placeholder="Ajoutez un commentaire"
                    style={{
                        borderRadius: '8px',
                        padding: '10px',
                        border: '1px solid #ccc',
                        width: '100%',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                    }}
                />
                <DefaultCommentSuggestions statutCommande={getOrderState()} onSelect={setCommentaire}/>
            </div>
            {/* Submit button */}
            <div style={{display: 'flex', justifyContent: 'center', marginTop: '20px'}}>
                <Button
                    type="primary"
                    disabled={cartItems.length === 0}
                    onClick={handleSaveAll}
                    style={{
                        backgroundColor: '#1890ff',
                        borderColor: '#1890ff',
                        borderRadius: '6px',
                        width: '50%',
                        fontSize: '16px',
                        padding: '12px',
                        color: '#fff'
                    }}
                >
                    Enregistrer la commande
                </Button>
            </div>
        </Modal>

    );
};

export default OrderModal;
